import { useEffect, useState } from 'react'

import moment from 'moment'
import * as Sentry from '@sentry/react'

import { getAuthenticatedUser, isAuthenticated } from 'components/auth/'
import { getUserBookmarks } from 'components/common/api/Bookmarks'
import { getUserRatings } from 'components/common/api/Ratings'
import { getCurrentSubscription } from 'components/common/api/Payments'
import { getCourses } from 'components/common/api/Courses'
import { getAllCourseProgress } from 'components/common/api/CourseProgress'
import { ROUTE_COURSES_ALL, ROUTE_LOGOUT } from '../helpers/Paths'
import { identifyKlaviyoUser } from '../api/UserEvents'
import { getSavedUserAssessmentPlan } from '../api/Users'
import { getTodayUserCheckin, getSummaryUserCheckin } from '../api/Checkins'
import { app } from '../../../config'

/**
 * User Initialization Hook -- Pulls the user's subscription, courses, progress, bookmarks, and ratings
 * @returns An object containing the user's subscription, courses, progress, bookmarks, and ratings
 * along with functions to update them
 */
const useInitializeUser = cache => {
  const [isAuthed, setIsAuthed] = useState(isAuthenticated())

  const [userProgram, setUserProgram] = useState()
  const [userSubscription, setUserSubscription] = useState({ active: true }) // Default to active, per ENG-343
  const [courses, setCourses] = useState()
  const [progress, setProgress] = useState()
  const [bookmarks, setBookmarks] = useState()
  const [ratings, setRatings] = useState()
  const [todayUserCheckin, setTodayUserCheckin] = useState()
  const [summaryUserCheckin, setSummaryUserCheckin] = useState()

  // Private helper function to determine if the user has premium access
  // User has access if their plan_id is greater than 0 or if their subscription has not ended
  const _hasPremiumAccess = () =>
    userSubscription.plan_id > 0 ||
    (userSubscription.ends_on !== '' &&
      moment(userSubscription.ends_on).diff(moment(), 'days', true) > 0)

  const [userHasPremiumAccess, setUserHasPremiumAccess] =
    useState(_hasPremiumAccess)

  // Helper function to determine if we should initialize the user
  const shouldInitializeUser =
    cache && // We have a cache
    isAuthed && // We are authenticated
    userSubscription.plan_id === undefined && // We don't have a subscription
    !window.location.pathname.includes(ROUTE_LOGOUT) // We are not logging out

  useEffect(() => {
    // Return early if we are not authenticated, don't have a cache or if we already have a subscription
    if (!shouldInitializeUser) return

    // Get the user before we proceed
    const user = getAuthenticatedUser()

    // Return early if we don't have a user
    if (!user) return

    // Now let's get the user's subscription, courses, progress, bookmarks, and ratings
    // TODO: Find ways to delay fetching these until they are needed
    Promise.allSettled([
      // Pull course info from cache, if available
      cache.tryGetAsyncResult(ROUTE_COURSES_ALL, getCourses, this),

      // TODO: Replace with single API call in ENG-354
      getAllCourseProgress(),

      // Get user's bookmarks
      getUserBookmarks({
        category: null,
        limit: 1000
      }),

      // Get user's ratings
      getUserRatings({
        category: null,
        limit: 1000
      }),

      // Get user's subscription
      getCurrentSubscription(),

      // Get user's assessment plan
      getSavedUserAssessmentPlan(),

      // Get today user checkin
      getTodayUserCheckin(),

      // Get summary user checkin
      getSummaryUserCheckin()
    ])
      .then(results => {
        // Use a fallback subscription if we failed to get the user's subscription
        let subscription = results[4].value || {
          active: true,
          plan_id: 0 // Set to "free trial" mode
        }

        // If the user is part of an organization, use the organization's subscription
        if (user.organization)
          subscription.plan_id = app.plans.featured_ids.annual

        // If there was a problem getting the user's subscription, log it
        if (results[4].status === 'rejected') {
          console.error(results[4].reason)
        } else {
          // Otherwise, let's update the subscription records in our trackers
          identifyKlaviyoUser(user, subscription)
        }

        // Update the auth state
        setIsAuthed(true)

        // Update the user's premium access if they are part of an organization
        setUserHasPremiumAccess(subscription.plan_id > 0)

        // Set the courses
        setCourses(results[0].value)

        // Set the progress
        setProgress(results[1].value)

        // Set the bookmarks
        setBookmarks(results[2].value)

        // Set the ratings
        setRatings(results[3].value)

        // Set the subscription
        setUserSubscription(subscription)

        // Set the user's program
        setUserProgram(results[5].value)

        // today use checkin
        setTodayUserCheckin(results[6].value)

        // summary user checkin
        setSummaryUserCheckin(results[7].value)
      })
      .catch(error => {
        Sentry.captureException(error)
        console.error(error)
      })
  }, [cache, shouldInitializeUser])

  return {
    courses,
    progress,
    setProgress,
    bookmarks,
    setBookmarks,
    ratings,
    setRatings,
    userSubscription,
    userHasPremiumAccess,
    setUserSubscription,
    isAuthed,
    setIsAuthed,
    userProgram,
    setUserProgram,
    setTodayUserCheckin,
    todayUserCheckin,
    setSummaryUserCheckin,
    summaryUserCheckin
  }
}

export default useInitializeUser
